import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const WISHLIST_STORAGE_KEY = "petshop_wishlist";

function loadWishlist(): string[] {
    try {
        const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function saveWishlist(ids: string[]) {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(ids));
}

interface WishlistContextType {
    isWishlisted: (productId: string) => boolean;
    toggleWishlist: (productId: string) => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: ReactNode }) {
    const [ids, setIds] = useState<Set<string>>(() => new Set(loadWishlist()));

    const toggleWishlist = useCallback((productId: string) => {
        setIds((current) => {
            const next = new Set(current);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            saveWishlist(Array.from(next));
            return next;
        });
    }, []);

    const isWishlisted = useCallback((productId: string) => ids.has(productId), [ids]);

    const value = useMemo(() => ({ isWishlisted, toggleWishlist }), [isWishlisted, toggleWishlist]);

    return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
    const context = useContext(WishlistContext);
    if (!context) {
        throw new Error("useWishlist must be used within a WishlistProvider");
    }
    return context;
}
